import { useEffect, useState, useCallback } from "react";
import { VehiclesAPI } from "@/lib/api";
import logger from "@/lib/logger";

/**
 * Shared hook — the ONLY sanctioned way for a component to read a
 * vehicle's current odometer. Mirrors the backend `OdometerService`, so
 * when an OEM telemetry provider is wired in (Tesla / Rivian / Ford /
 * Hyundai / Smartcar), the value UI sees flips over automatically — no
 * component changes required.
 *
 * Do NOT compute odometer locally by summing trips, reading vehicle.
 * current_odometer_km, or copying `last_odometer_km` off /last-telemetry.
 * If a new consumer needs the current odometer, wire it via this hook.
 *
 * Returns:
 *   {
 *     km,           // number | null (null while loading OR when no data)
 *     source,       // "manual_baseline" | "trip" | "energy" | "telemetry_<vendor>" | "none" | null
 *     recordedAt,   // ISO date string | null
 *     confidence,   // 0–1
 *     loading,
 *     error,        // Error | null
 *     refresh,      // () => Promise — force re-fetch (e.g. after logging a trip)
 *   }
 */
export default function useVehicleOdometer(vehicleId) {
  const [state, setState] = useState({
    km: null,
    source: null,
    recordedAt: null,
    confidence: 1,
    loading: !!vehicleId,
    error: null,
  });

  const load = useCallback(async () => {
    if (!vehicleId) {
      setState((s) => ({ ...s, loading: false }));
      return;
    }
    setState((s) => ({ ...s, loading: true, error: null }));
    try {
      const r = await VehiclesAPI.odometer(vehicleId);
      setState({
        km: typeof r.km === "number" ? r.km : null,
        source: r.source || null,
        recordedAt: r.recorded_at || null,
        confidence: typeof r.confidence === "number" ? r.confidence : 1,
        loading: false,
        error: null,
      });
    } catch (err) {
      logger.warn("useVehicleOdometer: fetch failed", err);
      setState({
        km: null,
        source: null,
        recordedAt: null,
        confidence: 0,
        loading: false,
        error: err,
      });
    }
  }, [vehicleId]);

  useEffect(() => {
    load();
  }, [load]);

  return { ...state, refresh: load };
}

/**
 * Presentation helper — turns a `source` tag into a short human badge.
 * Kept here (not in each component) so every consumer renders the tag
 * identically and gains new OEM labels for free.
 */
export function odometerSourceLabel(source) {
  if (!source || source === "none") return "";
  if (source === "manual_baseline") return "Baseline";
  if (source === "trip") return "From trips";
  if (source === "energy") return "From charges";
  if (source.startsWith("telemetry_")) {
    // e.g. telemetry_tesla → "Tesla · Live"
    const vendor = source.slice("telemetry_".length);
    return `${vendor.charAt(0).toUpperCase() + vendor.slice(1)} · Live`;
  }
  return source;
}
